/**
 * Pure logic for the widget's compact model picker. The server (/v1/status) already filters the
 * catalog to the models THIS caller may use (enabled + role-allowed) and marks the default; the
 * widget only has to (a) decide whether a picker is worth showing, (b) reconcile the persisted
 * per-app choice against the currently visible set, and (c) turn the choice into the optional
 * `model` override sent on asks. Extracted from GyroAssistant so it can be unit-tested.
 */
const RESPONSE_MODES = [
    { id: 'fast', label: 'Fast', description: 'Quick answers for everyday questions.', effort: 'low' },
    { id: 'balanced', label: 'Balanced', description: 'A balance of speed and thoughtful answers.', effort: 'medium' },
    { id: 'high', label: 'High', description: 'More thinking for questions with a few moving parts.', effort: 'high' },
    { id: 'deep', label: 'Deep', description: 'Take more time to work through complex tasks.', effort: 'xhigh' },
    { id: 'ultra', label: 'Ultra', description: 'Gyro at its most thorough. Allow extra time.', effort: 'max' },
];
/** Depth belongs to the selected model; changing it must never silently switch models. */
export function responseModes(models, selectedId = '') {
    const model = models.find(candidate => candidate.id === selectedId);
    if (!model)
        return RESPONSE_MODES.map(({ effort: _effort, ...mode }) => ({
            ...mode,
            ...(mode.id === 'balanced' ? { label: 'Auto' } : {}),
            disabled: mode.id !== 'balanced' || models.length === 0,
            description: mode.id === 'balanced' ? 'Gyro uses your organization’s default model and automatic routing.' : 'Choose a model to set its thinking level.',
        }));
    return RESPONSE_MODES.map(({ effort, ...mode }) => {
        const supported = Boolean(model?.supportedEfforts?.includes(effort));
        const available = Boolean(model && (supported || mode.id === 'balanced'));
        return { ...mode, disabled: !available, model: available ? model?.id : undefined,
            ...(supported ? { effort } : {}),
            ...(!available ? { description: model
                    ? 'Not available for this model. Choose a model that supports this thinking level.'
                    : 'Models are loading.' } : {}),
            ...(available && !supported ? { description: model?.supportedEfforts === undefined
                    ? 'Uses this model’s default thinking. More levels require support from your Gyro service.'
                    : 'Uses this model’s default thinking.' } : {}),
        };
    });
}
export function resolveResponseMode(options, stored) {
    return options.find(mode => mode.id === stored && !mode.disabled)
        ?? options.find(mode => mode.id === 'balanced')
        ?? { id: 'balanced', label: 'Balanced', description: 'Models are loading.', disabled: true };
}
/** Show the picker only when the user actually has a choice (more than one visible model). */
export function shouldShowPicker(models) {
    return (models?.length ?? 0) > 1;
}
/**
 * Reconcile a persisted choice with the visible set. Returns the chosen model id, or '' meaning
 * "use the server default" — which is also the graceful fallback when the stored choice has
 * disappeared (model disabled, role revoked, catalog edited).
 */
export function resolveModelChoice(models, storedId) {
    if (!storedId || !models)
        return '';
    // The High tier's model is never a saved choice (owner, 2026-10-17): a stored pick of it, however it got there, is dropped.
    return models.some((m) => m.id === storedId && m.tier !== 'high') ? storedId : '';
}
export const MODEL_TIERS = ['fast', 'balanced', 'high'];
const TIER_LABELS = { fast: 'Fast', balanced: 'Balanced', high: 'High' };
const TIER_DESCRIPTIONS = {
    fast: 'Quick answers for everyday questions.',
    balanced: 'A balance of speed and thoughtful answers. Every new chat starts here.',
    high: 'Deepest thinking, slowest, costs the most. Resets to the default model for each new chat.',
};
/** The names people know the catalog's usual ids by; the fallback when an entry's label is no name of its own. */
const KNOWN_MODEL_NAMES = Object.freeze({
    'claude-opus-5-5': 'Opus 5.5',
    'claude-sonnet-5-5': 'Sonnet 5.5',
    'claude-sonnet-5': 'Sonnet 5',
    'claude-fable-5-1': 'Fable 5.1',
});
/** A label that names nothing: a routing word (the seed's Fast and High, an admin's Best or Default) or the id itself. */
const ROUTING_WORD = /^(best|fast|balanced|high|default|recommended|quality|smart)(?:\s+model)?$/i;
/**
 * A model's name for people: its label when that is a real name; else, when the label is a routing word, missing, or the
 * id repeated (the seed labels the configured model with its id), the known name of its id, or one read from the id.
 */
export function modelDisplayName(model) {
    const label = (model.label ?? '').trim();
    if (label && !ROUTING_WORD.test(label) && label.toLowerCase() !== model.id.trim().toLowerCase())
        return label;
    const known = KNOWN_MODEL_NAMES[model.id];
    if (known)
        return known;
    const claude = /^claude-(opus|sonnet|haiku|fable|mythos)-(\d{1,2})(?:-(\d{1,2}))?(?:-\d{8})?$/i.exec(model.id);
    if (claude)
        return `${claude[1][0].toUpperCase()}${claude[1].slice(1)} ${claude[2]}${claude[3] ? `.${claude[3]}` : ''}`;
    return model.id;
}
/** The catalog entry a tier runs on, as /v1/status lists it; undefined when the caller's catalog has none for it. */
export function tierModel(models, tier) {
    if (tier === 'high')
        return models.find(model => model.tier === 'high');
    if (tier === 'fast')
        return models.find(model => model.tier === 'fast') ?? models.find(model => /^fast$/i.test(model.label.trim()) && model.tier !== 'high');
    return models.find(model => model.default) ?? models.find(model => !model.tier);
}
/** The segmented control's three entries; a tier the catalog has no model for is shown disabled, never hidden. */
export function modelTierOptions(models) {
    return MODEL_TIERS.map(id => {
        const model = tierModel(models, id);
        const label = TIER_LABELS[id];
        const description = id === 'high' && model?.description ? model.description : TIER_DESCRIPTIONS[id];
        const title = model ? `${modelDisplayName(model)} · ${label} — ${description}` : models.length ? `${label} is not available here.` : 'Models are loading.';
        return { id, label, description, title, disabled: !model, ...(model ? { model } : {}) };
    });
}
/** Whether the control is worth drawing: at least one tier besides Balanced can be picked. */
export function shouldShowTiers(models) {
    return Boolean(models && modelTierOptions(models).some(option => option.id !== 'balanced' && !option.disabled));
}
/**
 * What an ask carries for the chat's tier: `modelTier` for Fast or High, nothing for Balanced (the service's default,
 * exactly as an ask with no override). The service resolves the tier itself, so no model id or depth travels with it.
 */
export function tierRequestOptions(tier) {
    return tier === 'balanced' ? {} : { modelTier: tier };
}
/** The `model` override to send on an ask: undefined when the user is on the default. */
export function modelOverrideFor(choice) {
    return choice || undefined;
}
/**
 * Shared outgoing options for browser/native asks and compaction. Auto sends neither field, so it
 * cannot accidentally pin the advertised default or a stale saved depth. The optional depth is
 * only for desktop asks; compaction and the embedded picker leave thinking to the server.
 */
export function modelRequestOptions(models, selectedId, modeId) {
    const model = resolveModelChoice(models, selectedId);
    if (!model)
        return {};
    const selected = modeId === undefined ? undefined : resolveResponseMode(responseModes(models, model), modeId);
    return { model, ...(selected?.effort ? { effort: selected.effort } : {}) };
}
/** Label for the picker's "Default" option, naming the default model when the server marked one. */
export function defaultOptionLabel(models) {
    const def = models?.find((m) => m.default);
    return def ? `Default — ${def.label}` : 'Default';
}
/** Every entry /v1/status lists (already the models this caller may use), in the catalog's order. */
export function modelMenuEntries(models) {
    return models.map(model => {
        const high = model.tier === 'high';
        const tag = high ? 'High · per chat only' : model.tier === 'fast' ? 'Fast' : model.default ? 'Balanced' : undefined;
        return { id: model.id, name: modelDisplayName(model), ...(tag ? { tag } : {}), ...(high ? { note: model.description || TIER_DESCRIPTIONS.high } : {}), high, model };
    });
}
/**
 * The model the open chat runs on: the High entry while the chat is on High; else the saved pick when the catalog still
 * lists it; else the default. Undefined until the catalog has loaded.
 */
export function chatModel(models, tier, choice) {
    if (tier === 'high')
        return tierModel(models, 'high') ?? tierModel(models, 'balanced');
    return (choice ? models.find(model => model.id === choice && model.tier !== 'high') : undefined) ?? tierModel(models, 'balanced');
}
/** The thinking button's stops: the model's server-verified levels, in order (none while the catalog loads). */
export function thinkingLevels(model) {
    return model?.supportedEfforts?.filter((level, index, all) => all.indexOf(level) === index) ?? [];
}
/** The level a chat starts at on `levels`: medium where the model has it, else its first stop. */
export function defaultThinkingLevel(levels) {
    return levels.includes('medium') ? 'medium' : levels[0];
}
/** The words the thinking button, the name under the thumb and the axis show for each level (owner, 2026-10-19); the raw value still travels as `effort`. */
const THINKING_LEVEL_LABELS = Object.freeze({ low: 'Low', medium: 'Medium', high: 'High', xhigh: 'Very high', max: 'Max' });
export function thinkingLevelLabel(level) {
    return THINKING_LEVEL_LABELS[level] ?? level;
}
/** The deepest stop: what the High tier always runs at (the service fixes High's depth; the slider is locked there). */
export function deepestThinkingLevel(levels) {
    return levels.length ? levels[levels.length - 1] : undefined;
}
/**
 * What an ask carries for the chat's model and thinking. High: `modelTier: 'high'` alone (the service runs its entry at
 * the deepest thinking and ignores a model or depth sent beside it). Otherwise the saved model id when one is picked (as
 * the dropdown before the tiers sent it; nothing for the default) and `effort` only when the slider was moved in this
 * chat ('' = the model's own default, so the service's cheap routing for plain asks is untouched).
 */
export function chatRequestOptions(tier, choice, effort) {
    if (tier === 'high')
        return { modelTier: 'high' };
    return { ...(choice ? { model: choice } : {}), ...(effort ? { effort } : {}) };
}
//# sourceMappingURL=modelChoice.js.map